import { useState, useRef, useEffect } from "react";
import { Attachment as AttachmentProto } from "protogen/common_pb";
import {
  Box,
  Typography,
  IconButton,
  Slider,
  CircularProgress,
  Collapse,
} from "@mui/material";
import { Play, Pause, MoreVertical, ChevronDown } from "lucide-react";
import PopperMenu, { createMenuOption } from "./PopperMenu";

export type VoiceMemoProps = {
  attachment: AttachmentProto;
  loading?: boolean;
  error?: string;
  duration?: string; // e.g. "0:32"
  transcript?: string;
};

const errorBorder = { border: "2px solid red", boxShadow: "0 0 3px red" };

export default ({
  attachment,
  loading,
  error,
  duration,
  transcript,
}: VoiceMemoProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);
    const handleLoaded = () => setAudioDuration(audio.duration);
    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoaded);
    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoaded);
    };
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleProgressChange = (_: any, value: number | number[]) => {
    const newTime = Array.isArray(value) ? value[0] : value;
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const totalDuration =
    duration || (audioDuration ? formatTime(audioDuration) : "0:00");

  const handleMenuToggle = () => setMenuOpen((open) => !open);
  const handleMenuClose = () => setMenuOpen(false);

  const menuOptions = [
    createMenuOption(
      "Download",
      () => {
        const link = document.createElement("a");
        link.href = attachment.url;
        link.target = "_blank";
        link.setAttribute(
          "download",
          attachment.filename || `voice-memo-${new Date().toISOString()}.mp3`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        handleMenuClose();
      },
      "download",
    ),
  ];

  return (
    <Box
      sx={{
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#FFF",
        padding: "16px 20px",
        border: "1px solid #F2F2F2",
        boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.11)",
        background: "#fff",
        ...(error ? errorBorder : {}),
        ...(loading ? { filter: "blur(2px)" } : {}),
      }}
    >
      <Box sx={{}}>
        <Box display="flex" alignItems="center" gap="8px">
          <IconButton
            onClick={togglePlay}
            color="primary"
            sx={{
              width: "36px",
              height: "36px",
              bgcolor: "primary.main",
              color: "#fff",
              "&:hover": { bgcolor: "primary.dark" },
            }}
            disabled={loading || !!error}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </IconButton>
          <Box display="flex" alignItems="center" flexGrow={1} gap="4px">
            <Typography
              variant="caption"
              sx={{ minWidth: "38px", textAlign: "left" }}
            >
              {formatTime(currentTime)}
            </Typography>
            <Slider
              min={0}
              max={audioDuration || 100}
              value={currentTime}
              onChange={handleProgressChange}
              sx={{ flexGrow: 1, minWidth: "40px", marginLeft: "6px" }}
              disabled={loading || !!error}
            />
            <Typography
              variant="caption"
              sx={{ minWidth: "38px", textAlign: "right" }}
            >
              {totalDuration}
            </Typography>
          </Box>
          <IconButton
            size="small"
            ref={menuAnchorRef}
            onClick={handleMenuToggle}
          >
            <MoreVertical size={16} />
          </IconButton>
          <PopperMenu
            open={menuOpen}
            anchorRef={menuAnchorRef}
            handleClose={handleMenuClose}
          >
            {menuOptions}
          </PopperMenu>
        </Box>

        {transcript && (
          <Box marginTop="16px">
            <Box
              display="flex"
              alignItems="center"
              columnGap="8px"
              sx={{ cursor: "pointer" }}
              onClick={() => setIsExpanded((v) => !v)}
            >
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                Transcription
              </Typography>
              <ChevronDown
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  color: "text.secondary",
                }}
              />
            </Box>
            <Collapse in={isExpanded}>
              <Box
                marginTop="8px"
                padding="16px"
                bgcolor="#f7f7f7"
                borderRadius="8px"
              >
                <Typography variant="body2" color="text.secondary">
                  {transcript}
                </Typography>
              </Box>
            </Collapse>
          </Box>
        )}

        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            marginTop="16px"
          >
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ marginTop: "16px" }}>
            {error}
          </Typography>
        )}
        <audio
          ref={audioRef}
          src={attachment.url}
          preload="metadata"
          style={{ display: "none" }}
        />
      </Box>
    </Box>
  );
};
